import React, { useEffect, useState } from "react";
import GenericModal from "@ui-components/Modal/GenericModal";
import GenericButton from "@ui-components/Button/GenericButton";
import GenericDropdown from "@ui-components/Dropdown/GenericDropdown";
import type { ReportType } from "./ReportsData";
import type { GenerateReportBody } from "./request/request";
import {
  buildRecentPeriods,
  findPeriodByLabel,
  type ReportPeriod,
} from "./reportPeriods";

/** The two flavours, in the order they read best in the dropdown. */
const TYPE_OPTIONS: { label: string; value: ReportType; hint: string }[] = [
  {
    label: "Detailed",
    value: "detailed",
    hint: "The one-page summary plus a full section per service.",
  },
  {
    label: "Summary",
    value: "summary",
    hint: "A single page with one line per service.",
  },
];

const DEFAULT_TYPE_LABEL = "Detailed";

export interface GenerateReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (body: GenerateReportBody) => void;
  /** The request is in flight; the form stays mounted but locked. */
  isSubmitting: boolean;
  /** Why the last attempt was rejected, shown in place rather than as a toast. */
  errorMessage: string | null;
}

/**
 * Asks for the two things `POST /reports/generate` needs — type and month.
 *
 * The modal is a controlled form only: it never calls the API and never closes
 * itself, so the page stays the single place that knows whether a generation
 * succeeded and the list has to be re-read.
 */
const GenerateReportModal: React.FC<GenerateReportModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting,
  errorMessage,
}) => {
  const [periods, setPeriods] = useState<ReportPeriod[]>(() =>
    buildRecentPeriods(new Date())
  );
  const [typeLabel, setTypeLabel] = useState<string>(DEFAULT_TYPE_LABEL);
  const [periodLabel, setPeriodLabel] = useState<string>(
    () => periods[0]?.label ?? ""
  );

  // Each opening re-reads the clock and starts from the defaults, so a page
  // left open across a month boundary still offers the months that are
  // actually complete, and a previous failed attempt is not pre-filled.
  useEffect(() => {
    if (!isOpen) return;
    const fresh = buildRecentPeriods(new Date());
    setPeriods(fresh);
    setTypeLabel(DEFAULT_TYPE_LABEL);
    setPeriodLabel(fresh[0]?.label ?? "");
  }, [isOpen]);

  const selectedType = TYPE_OPTIONS.find((o) => o.label === typeLabel);
  const selectedPeriod = findPeriodByLabel(periods, periodLabel);
  const canSubmit = Boolean(selectedType && selectedPeriod) && !isSubmitting;

  const handleSubmit = () => {
    if (!selectedType || !selectedPeriod) return;
    onSubmit({
      year: selectedPeriod.year,
      month: selectedPeriod.month,
      reportType: selectedType.value,
    });
  };

  return (
    <GenericModal
      isOpen={isOpen}
      onClose={onClose}
      title="Generate report"
      size="md"
      overflowY="visible"
      footer={
        <div className="flex items-center justify-end gap-2">
          <GenericButton
            variant="secondary"
            size="md"
            label="Cancel"
            onClick={onClose}
            disabled={isSubmitting}
          />
          <GenericButton
            variant="brand"
            size="md"
            label="Generate"
            onClick={handleSubmit}
            loading={isSubmitting}
            disabled={!canSubmit}
          />
        </div>
      }
    >
      <div className="flex flex-col gap-5 py-2">
        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-semantic-text-primary">
            Report type
          </label>
          <GenericDropdown
            items={TYPE_OPTIONS.map((option) => option.label)}
            value={typeLabel}
            onSelect={setTypeLabel}
            disabled={isSubmitting}
            widthClass="w-full"
            size="md"
          />
          {selectedType && (
            <p className="text-xs text-semantic-text-secondary">
              {selectedType.hint}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-semantic-text-primary">
            Month
          </label>
          <GenericDropdown
            items={periods.map((period) => period.label)}
            value={periodLabel}
            onSelect={setPeriodLabel}
            disabled={isSubmitting}
            widthClass="w-full"
            size="md"
          />
          <p className="text-xs text-semantic-text-secondary">
            Only complete months are offered — the current month is still
            accruing data.
          </p>
        </div>

        {errorMessage && (
          <p
            role="alert"
            className="text-sm text-semantic-text-danger bg-semantic-bg-danger-secondary border border-semantic-border-danger-primary rounded-md px-3 py-2"
          >
            {errorMessage}
          </p>
        )}
      </div>
    </GenericModal>
  );
};

export default GenerateReportModal;
